import { AddressComponents } from "components/AddressComponent/AddressComponent";
import { Divider } from "components/design/Divider";
import { TableUI } from "components/design/TableUI";
import { AvatarUpload } from "components/DesignSystem/AvatarGroup/AvatarUpload";
import { Button } from "components/DesignSystem/Button/Button";
import { CheckboxField } from "components/DesignSystem/Checkbox/CheckboxField";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import Modal from "components/DesignSystem/Modal/Modal";
import { Label, TextInput } from "components/DesignSystem/TextInput/TextInput";
import { PlusIcon } from "components/icons/PlusIcon";
import { InfoToolTip } from "components/Vendors/AddVendor/AddVendorModal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { PriceInput } from "components/PriceInput/PriceInput";
import { YYYY } from "constants/date";
import { SOURCE } from "constants/vendor";
import dayjs from "dayjs";
import { Field, FieldProps, FormikValues, useFormikContext } from "formik";
import { vendorEditDetailsSchema } from "formValidations/vendorSchema";
import { useToast } from "hooks/useToast";
import { useMemo } from "react";
import randomBytes from "randombytes";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { useGetCurrenciesQuery } from "store/apis/invoices";
import { useEditVendorsMutation } from "store/apis/vendors";
import { AmountPerSeason, Vendor } from "types/Models/vendors";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { capitalize } from "utils/capitalize";

type SeasonItem = { id: string } & AmountPerSeason;

type EditVendorSchema = {
  name: string;
  email: string;
  address: string;
  payment_instructions: string;
  source: keyof typeof SOURCE;
  currency: string;
  total_amount: number;
  amounts_per_season: SeasonItem[];
  is_related_party: boolean;
};

const createColumn = createColumnHelper<SeasonItem>();

const createSeasonColumns = (selectedSeason?: string) => [
  createColumn.accessor("season", {
    header: () => <Label>Season</Label>,
    size: 50,
    cell: ({ row: { id } }) => (
      <Field name={`amounts_per_season[${id}].season`}>
        {({ field }: FieldProps) => (
          <DateInput
            {...field}
            name={field.name}
            showYearPicker
            showYearDropdown
            dateFormat="yyyy"
            hideError
            placeholder="YYYY"
            portalId={field.name}
            disabled={!!selectedSeason}
          />
        )}
      </Field>
    ),
  }),

  createColumn.accessor("total_amount", {
    header: () => (
      <div className="t-justify-start t-w-full t-flex">
        <Label>1099 AMOUNT</Label>
      </div>
    ),
    size: 50,
    cell: ({ row: { id } }) => (
      <PriceInput
        rightAlign
        name={`amounts_per_season[${id}].total_amount`}
        placeholder="Enter amount"
      />
    ),
  }),
];

const AmountsPerSeason = ({ selectedSeason }: { selectedSeason?: string }) => {
  const { values, setFieldValue } = useFormikContext<EditVendorSchema>();
  const { amounts_per_season } = values;

  const filteredData = useMemo(() => {
    if (!selectedSeason) return amounts_per_season;
    return amounts_per_season.filter(
      ({ season }) => season && dayjs(season).format(YYYY) === selectedSeason
    );
  }, [amounts_per_season, selectedSeason]);

  const seasonColumns = useMemo(
    () => createSeasonColumns(selectedSeason),
    [selectedSeason]
  );

  const table = useReactTable({
    data: filteredData,
    columns: seasonColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) =>
      String(amounts_per_season.findIndex((item) => item.id === row.id)),
  });

  const onAddMore = () => {
    setFieldValue("amounts_per_season", [
      ...amounts_per_season,
      { season: "", total_amount: "", id: randomBytes(10).toString("hex") },
    ]);
  };

  return (
    <div className="t-flex t-gap-1.5 t-flex-col">
      <span className="t-text-text-100 t-text-subtext">
        1099 Amounts per Season{" "}
        <InfoToolTip
          info={
            <>
              Sum of the payments made during the season
              <br />
              that qualify to be reported on IRS Form 1099.
            </>
          }
        />
      </span>
      <TableUI table={table} />
      {!selectedSeason && (
        <div className="t-mr-auto">
          <Button
            type="button"
            size="small"
            customType="ghost"
            onClick={onAddMore}
          >
            <span className="t-text-text-30 t-mr-1.5">
              <PlusIcon />
            </span>
            Add Another Payment
          </Button>
        </div>
      )}
    </div>
  );
};

const EditVendorForm = ({
  vendor,
  entityId,
  groupId,
  selectedSeason,
}: {
  vendor: Vendor;
  entityId: string;
  groupId: string;
  selectedSeason?: string;
}) => {
  const { values, setFieldValue, errors, touched } =
    useFormikContext<EditVendorSchema>();

  const { data: currenciesResponse } = useGetCurrenciesQuery();

  const currencyOptions = useMemo(() => {
    if (!currenciesResponse) return [];

    return Object.entries(currenciesResponse).map(([code, name]) => ({
      label: `${code} - ${name}`,
      value: code,
    }));
  }, [currenciesResponse]);

  const currentCurrency = currencyOptions.find(
    (currency) => currency.value === values.currency
  );

  const addressError =
    touched.address && errors.address ? errors.address : null;

  const selectedSource: OptionData = {
    value: values.source,
    label: capitalize(values.source),
  };

  const { w_form_season, source } = vendor || {};
  const isAmountEditAllowed = source === "MANUAL" || !!w_form_season;

  return (
    <div className="t-flex t-flex-col t-gap-4">
      <div className="t-flex t-items-center t-gap-4">
        <AvatarUpload variant="primary" alt={values.name} size="extra-large" />
        <TextInput
          required
          label="Name"
          name="name"
          placeholder="Enter vendor name"
        />
      </div>
      <div className="t-flex t-gap-5 sm:t-flex-col sm:t-gap-3">
        <TextInput
          block
          label="Email ID"
          name="email"
          placeholder="Enter vendor email"
        />
        <Combobox
          block
          withForm
          menuPortalTarget={document.body}
          label="Preferred Currency"
          name="currency"
          placeholder="Select a currency"
          options={currencyOptions}
          value={currentCurrency || null}
          components={{
            ClearIndicator: () => null,
          }}
        />
      </div>
      <div
        className={
          addressError
            ? "[&_.t-border]:!t-border-red [&_.t-border]:focus:!t-border-red [&_.t-border]:hover:!t-border-red"
            : ""
        }
      >
        <AddressComponents
          required={false}
          formPrefix=""
          entityId={entityId}
          groupId={groupId}
          selectedAddressId={values.address}
          label="Address"
          onAddressSelect={(address) =>
            setFieldValue("address", address?.uuid || "")
          }
        />
        {addressError && (
          <div className="t-mt-1.5 t-text-body-sm t-text-red">
            {addressError}
          </div>
        )}
      </div>
      <TextInput
        label="Payment Instructions"
        name="payment_instructions"
        placeholder="Enter Payment Instruction"
      />
      <Combobox
        isDisabled
        required
        menuPortalTarget={document.body}
        name="source"
        label="Source"
        options={[selectedSource]}
        value={selectedSource}
        withForm
      />
      <Divider type="dashed" />
      {source === "MANUAL" ? (
        <AmountsPerSeason selectedSeason={selectedSeason} />
      ) : (
        <PriceInput
          disabled={!isAmountEditAllowed}
          name="total_amount"
          label="Amount Paid"
          required
        />
      )}
      <CheckboxField
        name="is_related_party"
        checkboxProps={{ label: "Mark this vendor as related party" }}
      />
    </div>
  );
};

export const EditVendorDetails = ({
  isOpen,
  close,
  vendor,
  groupId,
  entityId,
  selectedSeason,
}: ModalProps & {
  vendor: Vendor;
  groupId: string;
  entityId: string;
  selectedSeason?: string;
}) => {
  const { alertToast, successToast } = useToast();
  const [editVendor, { isLoading }] = useEditVendorsMutation();

  const onSubmit = async (values: FormikValues) => {
    try {
      const amountPaidInSeason = values.amounts_per_season
        .filter(
          ({ season, total_amount }: AmountPerSeason) => season && total_amount
        )
        .map(({ season, total_amount }: AmountPerSeason) => ({
          season: season ? dayjs(season).format(YYYY) : "",
          total_amount,
        }));

      await editVendor({
        groupId,
        uuid: vendor.uuid,
        payload: {
          name: values.name,
          email: values.email,
          address: values.address,
          payment_instruction: values.payment_instructions,
          currency: values.currency,
          total_amount: values.total_amount,
          amount_paid_in_seasons:
            amountPaidInSeason.length > 0 ? amountPaidInSeason : undefined,
          is_related_party: values.is_related_party,
        },
      }).unwrap();
      successToast({ message: "Edits saved successfully" });
      close();
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content useCustomOverlay asChild size="large">
        <FormikForm
          initialValues={{
            name: vendor.name || "",
            email: vendor.email || "",
            address: vendor.address?.uuid || "",
            payment_instructions: vendor.payment_instructions || "",
            source: vendor.source,
            currency: vendor.currency || "",
            total_amount: vendor["1099_transaction_amount"] || 0,
            amounts_per_season:
              vendor.amount_paid_in_season?.length > 0
                ? vendor.amount_paid_in_season.map((item) => ({
                    ...item,
                    total_amount: String(item.total_amount),
                    id: randomBytes(10).toString("hex"),
                  }))
                : [
                    {
                      season: "",
                      total_amount: "",
                      id: randomBytes(10).toString("hex"),
                    },
                  ],
            is_related_party: vendor.is_related_party || false,
          }}
          validationSchema={vendorEditDetailsSchema}
          validateOnChange
          onSubmit={onSubmit}
        >
          {({ submitForm, isSubmitting, dirty }) => (
            <>
              <Modal.Header>
                <Modal.Title>Edit Vendor</Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body>
                <EditVendorForm
                  vendor={vendor}
                  groupId={groupId}
                  entityId={entityId}
                  selectedSeason={selectedSeason}
                />
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button disabled={isSubmitting}>Cancel</Button>
                </Modal.RawClose>
                <Button
                  disabled={!dirty || isLoading}
                  isLoading={isSubmitting || isLoading}
                  onClick={submitForm}
                  customType="primary"
                >
                  Save
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
